import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatMoney } from '@/utils/money';
import type { CustomerOption } from '@restaurant/api-contracts';
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useEffect, useMemo, useState } from 'react';
import {
  configError,
  initialSelections,
  isOptionFull,
  optionHint,
  optionRequired,
  optionShortage,
  selectionsToChoices,
} from '../lib/roundDraft';
import { effectiveSelectBounds } from '@restaurant/types';
import { useRoundDraft } from '../stores/roundDraft.store';
import { useDineInMenu } from '../hooks/useTableOrdering';

/**
 * Configure a product's options, then commit it to the round and pop back to
 * the menu. Reached only by navigation from AddRoundPage (product id + optional
 * edit key in the query); the line lands in the shared `useRoundDraft` store.
 */
export default function ConfigureItemPage({
  f7route,
}: {
  f7route: { query: { product?: string; edit?: string } };
}) {
  const tAll = useT();
  const t = tAll.tableOrdering.configureItem;
  const productId = f7route.query.product;
  const editKey = f7route.query.edit ? decodeURIComponent(f7route.query.edit) : null;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data: menu, isLoading } = useDineInMenu();
  const addLine = useRoundDraft((s) => s.addLine);

  const product = useMemo(() => {
    if (!menu || !productId) return null;
    for (const cat of menu.categories) {
      const p = cat.products.find((x) => x.id === productId);
      if (p) return p;
    }
    return null;
  }, [menu, productId]);

  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [flashedOptionId, setFlashedOptionId] = useState<string | null>(null);

  useEffect(() => {
    if (!flashedOptionId) return;
    const timer = setTimeout(() => setFlashedOptionId(null), 1800);
    return () => clearTimeout(timer);
  }, [flashedOptionId]);

  // Seed from the edited line's choices (if any), else the product defaults.
  useEffect(() => {
    if (!product) return;
    const existing = editKey
      ? useRoundDraft.getState().lines.find((l) => l.key === editKey)?.choices
      : undefined;
    setSelections(initialSelections(product, existing));
  }, [product, editKey]);

  if (isLoading) {
    return (
      <Page name="configure-item">
        <Navbar title={t.title} backLink={tAll.back} />
        <Block className="text-align-center" style={{ paddingTop: 48 }}>
          <Preloader />
        </Block>
      </Page>
    );
  }
  if (!product) {
    return (
      <Page name="configure-item">
        <Navbar title={t.title} backLink={tAll.back} />
        <EmptyState
          icon="exclamationmark_triangle"
          title={t.dishUnavailable}
          description={t.dishUnavailableDesc}
        />
      </Page>
    );
  }

  const toggleChoice = (opt: CustomerOption, choiceId: string) => {
    const picked = selections[opt.id] ?? [];
    if (opt.isMultiple && !picked.includes(choiceId) && isOptionFull(opt, picked.length)) {
      setFlashedOptionId(opt.id);
      return;
    }
    setSelections((prev) => {
      const cur = prev[opt.id] ?? [];
      let next: string[];
      if (opt.isMultiple) {
        if (cur.includes(choiceId)) {
          next = cur.filter((id) => id !== choiceId);
        } else {
          next = [...cur, choiceId];
        }
      } else {
        next = [choiceId];
      }
      return { ...prev, [opt.id]: next };
    });
  };

  const choices = selectionsToChoices(product, selections);
  const unit = product.price + choices.reduce((s, c) => s + c.priceCents, 0);
  const err = configError(product, selections, t);

  const commit = () => {
    if (err) return;
    addLine(product, choices, editKey);
    f7.views.current.router.back();
  };

  return (
    <Page name="configure-item">
      <Navbar title={product.name} backLink={tAll.back} />

      {product.options.map((opt) => {
        const sel = selections[opt.id] ?? [];
        const full = isOptionFull(opt, sel.length);
        const shortage = optionShortage(opt, t);
        const required = optionRequired(opt);
        const met = sel.length >= effectiveSelectBounds(opt).min;
        const flashed = flashedOptionId === opt.id;
        return (
          <div key={opt.id}>
            <BlockTitle className="to-cfg-opt-title">
              <span>{opt.name}</span>
              <span className="to-cfg-opt-hint">
                {optionHint(opt, sel.length, t)}
                {required && (
                  <>
                    {' · '}
                    {met ? (
                      <span className="to-cfg-opt-hint--ok">✓</span>
                    ) : (
                      <span className="to-cfg-opt-hint--missing">{t.required}</span>
                    )}
                  </>
                )}
              </span>
            </BlockTitle>
            {shortage && <p className="to-cfg-max-hint">{shortage}</p>}
            <List dividers strongIos insetIos className={flashed ? 'to-cfg-shake' : undefined}>
              {opt.choices.map((c) => (
                <ListItem
                  key={c.id}
                  radio={!opt.isMultiple}
                  checkbox={opt.isMultiple}
                  name={`opt-${opt.id}`}
                  value={c.id}
                  checked={sel.includes(c.id)}
                  disabled={!c.available}
                  className={full && !sel.includes(c.id) ? 'to-cfg-choice--blocked' : undefined}
                  title={c.name}
                  after={c.price > 0 ? `+${formatMoney(c.price, currency)}` : undefined}
                  onChange={() => toggleChoice(opt, c.id)}
                />
              ))}
            </List>
            {flashed && (
              <p className="to-cfg-max-hint">{t.maxReached(effectiveSelectBounds(opt).max ?? 0)}</p>
            )}
          </div>
        );
      })}

      <Block>
        <Button fill large round disabled={err !== null} onClick={commit}>
          {err ??
            (editKey
              ? t.updateItem(formatMoney(unit, currency))
              : t.addItem(formatMoney(unit, currency)))}
        </Button>
      </Block>
      <div style={{ height: 'calc(16px + var(--f7-safe-area-bottom, 0px))' }} />
    </Page>
  );
}
